import React, { PureComponent, ReactNode } from "react";
import { MusicNoteWithinChord } from "../../../wasm/pkg/wasm";
import { AppContextType } from "../../AppContext";
import { ICON_PLAY, ICON_SAVE } from "../../constants/iconConstants";
import { buildChordTypeFromSortedChordIntervals } from "../../functions/buildChordTypeFromSortedChordIntervals";
import { soundSingleChord } from "../../functions/playNotesAudio";
import { ChordInScaleEdit, toIntervalHalfSteps, toShortChordNameOverBass } from "../../models/wasm.model";
import IconButton from "../theme_reuse/IconButton";
import "./ChordEditor.css";
import ChordEditNoteRow, { NoteEditMeta } from "./custom_buttons/ChordEditNoteRow";

interface Props {
  chord: ChordInScaleEdit;
  onSave: (chord: ChordInScaleEdit) => void;
  putButtonsOnTop?: boolean;
}
interface State {
  notes: Array<NoteEditMeta>;
  bass_note?: MusicNoteWithinChord; // redundant with meta.isBassNote, but useful for print
}

export default class ChordEditor extends PureComponent<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: Props) {
    super(props);

    this.state = {
      ...zipperAudibleAndSilentNotes(props.chord),
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.chord?.root_note !== this.props.chord.root_note || prevProps.chord?.chord_type !== this.props.chord.chord_type) {
      this.setState({
        ...zipperAudibleAndSilentNotes(this.props.chord),
      });
    }
  }

  handlePlayChord = () => {
    const { notes } = this.state;
    const audible = notes.filter((n) => n.isSounded).map((n) => n.note);
    if (audible.length) {
      const arpeggiate = this.context.chordHistoryOptions.sounding.arpeggiation;
      const bassNoteId = this.state.bass_note?.note_id || undefined;
      soundSingleChord(audible, { arpeggiate, bassNoteId });
    }
  };
  handleSaveChord = () => {
    let chord = unzipperChordMeta(this.props.chord, this.state.notes);
    chord.chord_type = buildChordTypeFromSortedChordIntervals(this.state.notes);
    this.props.onSave(chord);
  };
  curryToggleAudible = (noteIndex: number) => () => {
    const { notes } = this.state;
    notes[noteIndex].isSounded = !notes[noteIndex].isSounded;
    this.setState({
      notes: [...notes],
    });
  };
  currySelectBassNote = (noteIndex: number) => () => {
    const { notes } = this.state;
    for (let i = 0; i < notes.length; i++) {
      notes[i].isBassNote = noteIndex === i;
    }
    let bass_note = this.props.chord.notes.find((n) => n.note_id === notes[noteIndex].note.note_id);
    if (!bass_note) {
      bass_note = this.props.chord.silent_notes.find((n) => n.note_id === notes[noteIndex].note.note_id);
    }
    this.setState({ notes, bass_note });
  };

  renderEditButtons() {
    return (
      <div className="chord-edit-buttons">
        <IconButton title="Play Chord" onClick={this.handlePlayChord} icon={ICON_PLAY} />
        <IconButton title="Save Changes" onClick={this.handleSaveChord} icon={ICON_SAVE} />
      </div>
    );
  }

  render(): ReactNode {
    const { notes, bass_note } = this.state;
    const { chord, putButtonsOnTop } = this.props;
    const chordName = toShortChordNameOverBass({ ...chord, chord_type: buildChordTypeFromSortedChordIntervals(notes) }, bass_note);

    return (
      <>
        <div className="chord-edit-header">
          <div className="chord-edit-name">{chordName}</div>
          {putButtonsOnTop ? this.renderEditButtons() : undefined}
        </div>
        <div className="chord-edit-note-list">
          <div className="chord-edit-note-list-header">
            <div>Note</div>
            <div>Interval</div>
            <div>Bass</div>
          </div>
          {notes.map((n, noteIndex) => {
            const key = `cen-${n.note.note_name}`;
            const rowKey = `bass-${chord.root_note.note_id}-${chord.chord_type}`;
            const className = `chord-edit-note ${n.isSounded ? "" : "silent-note"}`;
            return (
              <div key={key} className={className}>
                <ChordEditNoteRow
                  meta={n}
                  checkboxUniqueId={key}
                  radioBassSharedId={rowKey}
                  onToggleAudibility={this.curryToggleAudible(noteIndex)}
                  onSelectBassNote={this.currySelectBassNote(noteIndex)}
                />
              </div>
            );
          })}
        </div>
        {putButtonsOnTop ? undefined : this.renderEditButtons()}
      </>
    );
  }
}

ChordEditor.contextType = AppContextType;

function sortNotesAsc(a: MusicNoteWithinChord, b: MusicNoteWithinChord) {
  return toIntervalHalfSteps(a.interval.chord_int) - toIntervalHalfSteps(b.interval.chord_int);
}

/**
 * Hey look, data model regrets.
 */
function zipperAudibleAndSilentNotes(chord: ChordInScaleEdit): { notes: Array<NoteEditMeta>; bass_note: MusicNoteWithinChord } {
  let silentIndex = 0;
  let audibleIndex = 0;
  const { silent_notes, notes: audible_notes } = chord;
  silent_notes.sort(sortNotesAsc);
  audible_notes.sort(sortNotesAsc);
  let bass_note: MusicNoteWithinChord = audible_notes[0];
  let notesMeta: Array<NoteEditMeta> = [];
  while (silentIndex < silent_notes.length || audibleIndex < audible_notes.length) {
    const silentNotesRemain = silentIndex < silent_notes.length;
    const audibleNotesRemain = audibleIndex < audible_notes.length;
    const nextNoteIsAudible =
      audibleNotesRemain &&
      (!silentNotesRemain ||
        toIntervalHalfSteps(audible_notes[audibleIndex].interval.chord_int) <
          toIntervalHalfSteps(silent_notes[silentIndex].interval.chord_int));
    if (nextNoteIsAudible) {
      const isBassNote = chord.bass_note_id === audible_notes[audibleIndex].note_id;
      notesMeta.push({
        note: audible_notes[audibleIndex],
        isSounded: true,
        isBassNote,
      });
      if (isBassNote) {
        bass_note = audible_notes[audibleIndex];
      }
      audibleIndex++;
    } else {
      notesMeta.push({
        note: silent_notes[silentIndex],
        isSounded: false,
        isBassNote: chord.bass_note_id === silent_notes[silentIndex].note_id,
      });
      silentIndex++;
    }
  }
  return { notes: notesMeta, bass_note };
}

function unzipperChordMeta(chord: ChordInScaleEdit, meta: Array<NoteEditMeta>): ChordInScaleEdit {
  let notes: Array<MusicNoteWithinChord> = [];
  let silent_notes: Array<MusicNoteWithinChord> = [];
  let bass_note_id = chord.bass_note_id;
  meta.forEach((m) => {
    if (m.isSounded) {
      notes.push(m.note);
      if (m.isBassNote) {
        bass_note_id = m.note.note_id;
      }
    } else {
      silent_notes.push(m.note);
    }
  });

  return {
    ...chord,
    bass_note_id,
    notes,
    silent_notes,
  };
}
